import { clearAllSubmissions, clearAllDrafts, clearAllChunks } from '@/lib/db'

// Remembers which account's recordings are currently cached on this device so a
// different account signing in can have the previous account's local cache
// dropped (shared-browser privacy). Plain id, not a secret.
const ACTIVE_USER_KEY = 'app.activeUserId'

function readActiveUser(): string | null {
  try {
    return window.localStorage.getItem(ACTIVE_USER_KEY)
  } catch {
    return null
  }
}

function writeActiveUser(userSub: string): void {
  try {
    window.localStorage.setItem(ACTIVE_USER_KEY, userSub)
  } catch {
    // ignore storage failures (e.g. private mode)
  }
}

/**
 * Detect a user switch and purge the previous user's locally-cached recordings.
 * No-op for the same user, the first observed user, or an unauthenticated state.
 * Only the on-device cache is scoped — synced submissions live in AWS and are
 * re-fetched per user via /v1/me/submissions.
 */
export async function reconcileLocalStoreForUser(userSub: string | null): Promise<void> {
  if (!userSub) return

  const previous = readActiveUser()
  if (previous && previous !== userSub) {
    await Promise.allSettled([
      clearAllSubmissions(),
      clearAllDrafts(),
      clearAllChunks(),
    ])
    if (typeof window !== 'undefined') {
      // Nudge any mounted recording lists to reload from the now-empty store.
      window.dispatchEvent(new Event('recording-saved'))
    }
  }

  writeActiveUser(userSub)
}
